import { create } from 'zustand';
import type { AgentActivityFrame, AgentActivityPlanEntry, BackgroundRunState } from '../types/ws-frames';
import type { AgentActivitySnapshot } from '../lib/api';

/**
 * How many tool calls one agent keeps in one channel, matching the bound the
 * server holds its own ring to.
 *
 * The rail only ever carries upserts, never a removal, so a reader that did not
 * apply the same bound would keep entries the server has already dropped and
 * would then show a different list from the one a reload reads back.
 */
const ACTIVITY_RING_SIZE = 10;
const BACKGROUND_RUN_RING_SIZE = 32;
const BACKGROUND_RUN_ACTIVE_RETENTION_MS = 24 * 60 * 60 * 1000;
const BACKGROUND_RUN_TERMINAL_RETENTION_MS = 60 * 60 * 1000;

/** What a failed entry says when the report that created it patched something never seen. */
const NEVER_OPENED_REASON = 'reported without ever being opened';

export const ACTIVITY_STATUS_PENDING = 'pending';
export const ACTIVITY_STATUS_IN_PROGRESS = 'in_progress';
export const ACTIVITY_STATUS_WAITING = 'waiting';
export const ACTIVITY_STATUS_COMPLETED = 'completed';
export const ACTIVITY_STATUS_FAILED = 'failed';

/**
 * One tool call, as the panel holds it.
 *
 * Kind and status are strings rather than the wire's unions because both enums
 * are open upstream: an unrecognised value is relayed untouched and renders as
 * the generic case rather than failing anything.
 */
export interface AgentActivityEntry {
  id: string;
  label: string;
  description: string;
  kind: string;
  status: string;
  detail: string;
  paths: string[];
  parentId: string;
  subagent: boolean;
  background?: boolean;
  backgroundState?: string;
  followUpControl?: string;
  reason: string;
}

/**
 * One agent's work in one channel.
 *
 * `turnActive` is what the agent said through the turn boundary, never what its
 * entries imply, so an agent that finished leaving a tool call open is not
 * counted as working. `sequence` is that agent's own counter, and nothing older
 * than it is applied.
 */
export interface AgentPanelState {
  sequence: number;
  turnActive: boolean;
  plan: AgentActivityPlanEntry[];
  activities: AgentActivityEntry[];
  answer: string;
  reasoning: string;
  backgroundRuns: StoredBackgroundRun[];
}

export interface BackgroundRun {
  runId: string;
  label: string;
  state: BackgroundRunState;
  parentActivityId?: string;
  startedAt: number;
  finishedAt?: number;
  reason: string;
  stopSupported: boolean;
  stopUnsupportedReason: string;
}

interface StoredBackgroundRun extends BackgroundRun {
  observedAt: number;
  updatedSequence: number;
}

/** What an agent's line says about it. Only `waiting` asks the reader to do something. */
export type AgentActivityLine = 'working' | 'waiting' | 'idle';

export interface AgentActivityAtAsking {
  sequence: number;
  backgroundRunSequences: Record<string, number>;
}

/** The agent state held at the moment a snapshot was asked for. */
export type AgentActivitySnapshotBaseline = Record<string, AgentActivityAtAsking>;

interface AgentActivityState {
  byChannel: Record<string, Record<string, AgentPanelState>>;

  applyFrame: (frame: AgentActivityFrame, observedAt?: number) => void;
  applySnapshot: (
    channelId: string,
    snapshots: AgentActivitySnapshot[],
    askedWith: AgentActivitySnapshotBaseline,
    observedAt?: number,
  ) => void;
  clearExpiredBackgroundRuns: (now?: number) => void;
  clearChannel: (channelId: string) => void;
  reset: () => void;
}

const EMPTY_AGENT: AgentPanelState = {
  sequence: 0,
  turnActive: false,
  plan: [],
  activities: [],
  answer: '',
  reasoning: '',
  backgroundRuns: [],
};

/**
 * Whether an agent is working, waiting on a human, or neither.
 *
 * Waiting outranks working: "an agent is waiting for you" is actionable and "an
 * agent is working" is not. Waiting can only describe a turn that is running —
 * an entry left waiting by a turn that has ended describes nothing still
 * happening, and no later report is going to move it.
 */
export function agentActivityLine(agent: AgentPanelState): AgentActivityLine {
  if (!agent.turnActive) return 'idle';
  return agent.activities.some(entry => entry.status === ACTIVITY_STATUS_WAITING && !entry.subagent && !entry.background && !entry.parentId)
    ? 'waiting'
    : 'working';
}

/** How many agents in one channel are on the given line. */
export function countAgentsOnLine(
  agents: Record<string, AgentPanelState> | undefined,
  line: AgentActivityLine,
): number {
  if (!agents) return 0;
  let count = 0;
  for (const agent of Object.values(agents)) {
    if (agentActivityLine(agent) === line) count += 1;
  }
  return count;
}

/** A channel's agents in a fixed order, so a report about one never reorders the others. */
export function sortAgentsById(
  agents: Record<string, AgentPanelState> | undefined,
): [string, AgentPanelState][] {
  return Object.entries(agents ?? {}).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
}

export function isBackgroundRunActive(state: BackgroundRunState): boolean {
  return state === 'running' || state === 'waiting' || state === 'stopping';
}

export function countBackgroundRuns(runs: readonly BackgroundRun[]): {
  active: number;
  total: number;
} {
  return {
    active: runs.filter(run => isBackgroundRunActive(run.state)).length,
    total: runs.length,
  };
}

export function captureAgentActivitySnapshotBaseline(
  agents: Record<string, AgentPanelState> | undefined,
): AgentActivitySnapshotBaseline {
  return Object.fromEntries(
    Object.entries(agents ?? {}).map(([agentId, agent]) => [
      agentId,
      {
        sequence: agent.sequence,
        backgroundRunSequences: Object.fromEntries(
          agent.backgroundRuns.map(run => [run.runId, run.updatedSequence]),
        ),
      },
    ]),
  );
}

function isBackgroundRunTerminal(state: BackgroundRunState): boolean {
  return state === 'completed' || state === 'failed' || state === 'cancelled' || state === 'unknown';
}

function evictBackgroundRuns(runs: StoredBackgroundRun[]): StoredBackgroundRun[] {
  let kept = runs;
  while (kept.length > BACKGROUND_RUN_RING_SIZE) {
    const terminal = kept.findIndex(run => isBackgroundRunTerminal(run.state));
    const victim = terminal === -1 ? 0 : terminal;
    kept = [...kept.slice(0, victim), ...kept.slice(victim + 1)];
  }
  return kept;
}

function pruneExpiredBackgroundRuns(runs: StoredBackgroundRun[], now: number): StoredBackgroundRun[] {
  const kept = runs.filter(run => {
    const retention = isBackgroundRunTerminal(run.state)
      ? BACKGROUND_RUN_TERMINAL_RETENTION_MS
      : BACKGROUND_RUN_ACTIVE_RETENTION_MS;
    return run.observedAt >= now - retention;
  });
  return kept.length === runs.length ? runs : kept;
}

function mergeSnapshotBackgroundRuns(
  snapshotRuns: StoredBackgroundRun[],
  heldRuns: StoredBackgroundRun[],
  baseline: AgentActivityAtAsking | undefined,
): StoredBackgroundRun[] {
  const merged = snapshotRuns.slice();
  const baselineSequences = baseline?.backgroundRunSequences ?? {};

  for (const held of heldRuns) {
    if (baselineSequences[held.runId] === held.updatedSequence) continue;
    const index = merged.findIndex(run => run.runId === held.runId);
    if (index === -1) merged.push(held);
    else merged[index] = held;
  }

  return evictBackgroundRuns(merged);
}

function sameBackgroundRun(
  held: StoredBackgroundRun,
  incoming: BackgroundRun,
): boolean {
  return (
    held.runId === incoming.runId &&
    held.label === incoming.label &&
    held.state === incoming.state &&
    held.parentActivityId === incoming.parentActivityId &&
    held.startedAt === incoming.startedAt &&
    held.finishedAt === incoming.finishedAt &&
    held.reason === incoming.reason &&
    held.stopSupported === incoming.stopSupported &&
    held.stopUnsupportedReason === incoming.stopUnsupportedReason
  );
}

/**
 * Zips a new plan against the one held, keeping every entry that did not
 * change.
 *
 * The protocol gives plan entries no identity and requires the whole list on
 * every update, so position is all there is to match on. Rebuilding the list
 * each time would remount every row and make the section flicker at report
 * frequency; an unchanged list keeps its own reference so the section does not
 * re-render at all.
 */
function reconcilePlan(
  held: AgentActivityPlanEntry[],
  incoming: AgentActivityPlanEntry[],
): AgentActivityPlanEntry[] {
  const same =
    held.length === incoming.length &&
    held.every((entry, i) => entry.label === incoming[i].label && entry.status === incoming[i].status);
  if (same) return held;
  return incoming.map((entry, i) => {
    const previous = held[i];
    return previous && previous.label === entry.label && previous.status === entry.status ? previous : entry;
  });
}

/**
 * Opens an entry for an id nothing has opened yet, applying the same rule the
 * server does so a reload reads back what the live rail showed.
 *
 * A report carrying a label describes itself, which covers a call announcing
 * itself and equally an agent reporting one completed having never reported it
 * started. A report carrying none is a patch to something never seen, and it
 * opens as failed rather than being dropped, because agent and panel
 * disagreeing about what exists is worth showing. The exception is a patch
 * reporting a wait on a human: some tools produce no tool call at all, so their
 * permission prompt is legitimately the first thing seen about them.
 */
function openActivity(frame: AgentActivityFrame): AgentActivityEntry {
  const entry: AgentActivityEntry = {
    id: frame.activity_id ?? '',
    label: frame.label ?? '',
    description: frame.description ?? '',
    kind: frame.kind ?? '',
    status: frame.status ?? '',
    detail: frame.detail ?? '',
    paths: frame.paths ? [...frame.paths] : [],
    parentId: frame.parent_id ?? '',
    subagent: frame.subagent === true,
    background: frame.background === true,
    backgroundState: frame.background_state ?? '',
    followUpControl: frame.follow_up_control ?? '',
    reason: frame.reason ?? '',
  };
  if (entry.label === '' && entry.status !== ACTIVITY_STATUS_WAITING) {
    entry.status = ACTIVITY_STATUS_FAILED;
    if (entry.reason === '') entry.reason = NEVER_OPENED_REASON;
  } else if (entry.status === '') {
    entry.status = ACTIVITY_STATUS_PENDING;
  }
  return entry;
}

/**
 * Folds a report into an entry that already exists, answering the same entry
 * when it says nothing new.
 *
 * An absent field means unchanged, and on this wire an absent field and an
 * empty one arrive the same way, so both leave what is there alone. Paths
 * replace rather than merge, because an agent streaming a tool's input resends
 * the whole list. No status is refused: the protocol declares no transition
 * table, so whatever was last reported is what the entry says.
 */
function patchActivity(held: AgentActivityEntry, frame: AgentActivityFrame): AgentActivityEntry {
  const patched: AgentActivityEntry = {
    ...held,
    label: frame.label || held.label,
    description: frame.description || held.description,
    kind: frame.kind || held.kind,
    status: frame.status || held.status,
    detail: frame.detail || held.detail,
    // Being a container is a fact about the call and no producer un-says it: a
    // later report that omits it had nothing to add.
    subagent: held.subagent || frame.subagent === true,
    background: held.background || frame.background === true,
    backgroundState: frame.background_state || held.backgroundState,
    followUpControl: frame.follow_up_control || held.followUpControl,
    parentId: frame.parent_id || held.parentId,
    reason: frame.reason || held.reason,
  };
  if (frame.paths) patched.paths = [...frame.paths];

  const unchanged =
    patched.label === held.label &&
    patched.description === held.description &&
    patched.kind === held.kind &&
    patched.status === held.status &&
    patched.detail === held.detail &&
    patched.subagent === held.subagent &&
    patched.background === held.background &&
    patched.backgroundState === held.backgroundState &&
    patched.followUpControl === held.followUpControl &&
    patched.parentId === held.parentId &&
    patched.reason === held.reason &&
    patched.paths.length === held.paths.length &&
    patched.paths.every((path, i) => path === held.paths[i]);
  return unchanged ? held : patched;
}

/**
 * Brings the ring back inside its bound, taking the oldest finished entry first
 * so a tool running for the whole turn is never dropped for chatter that
 * finished after it started. When nothing has finished the oldest goes anyway:
 * the bound is what makes this state safe to keep.
 */
function evict(entries: AgentActivityEntry[]): AgentActivityEntry[] {
  let kept = entries;
  while (kept.length > ACTIVITY_RING_SIZE) {
    const finished = kept.findIndex(
      entry => entry.status === ACTIVITY_STATUS_COMPLETED || entry.status === ACTIVITY_STATUS_FAILED,
    );
    const victim = finished === -1 ? 0 : finished;
    kept = [...kept.slice(0, victim), ...kept.slice(victim + 1)];
  }
  return kept;
}

function upsertActivity(
  entries: AgentActivityEntry[],
  frame: AgentActivityFrame,
): AgentActivityEntry[] {
  const index = entries.findIndex(entry => entry.id === (frame.activity_id ?? ''));
  if (index === -1) return evict([...entries, openActivity(frame)]);
  const patched = patchActivity(entries[index], frame);
  if (patched === entries[index]) return entries;
  const next = entries.slice();
  next[index] = patched;
  return next;
}

/**
 * Folds one report into an agent's state, answering the same state when the
 * report changes nothing a reader could see.
 *
 * Identity is what stops the panel re-rendering at report frequency: the
 * producer coalesces to the newest value and sends absolute state, so the same
 * output tail arriving twice is ordinary rather than exceptional.
 */
function foldFrame(
  agent: AgentPanelState,
  frame: AgentActivityFrame,
  observedAt: number,
): AgentPanelState {
  switch (frame.shape) {
    case 'activity': {
      const activities = upsertActivity(agent.activities, frame);
      return activities === agent.activities ? agent : { ...agent, sequence: frame.sequence, activities };
    }
    case 'plan': {
      const plan = reconcilePlan(agent.plan, frame.plan ?? []);
      return plan === agent.plan ? agent : { ...agent, sequence: frame.sequence, plan };
    }
    case 'output': {
      const text = frame.text ?? '';
      if (frame.stream === 'reasoning') {
        return text === agent.reasoning ? agent : { ...agent, sequence: frame.sequence, reasoning: text };
      }
      if (frame.stream === 'answer') {
        return text === agent.answer ? agent : { ...agent, sequence: frame.sequence, answer: text };
      }
      return agent;
    }
    case 'turn': {
      const turnActive = frame.turn_state === 'started';
      return turnActive === agent.turnActive ? agent : { ...agent, sequence: frame.sequence, turnActive };
    }
    case 'background_run': {
      const runId = frame.run_id ?? '';
      if (!runId) return agent;
      const index = agent.backgroundRuns.findIndex(run => run.runId === runId);
      const held = index >= 0 ? agent.backgroundRuns[index] : undefined;
      const run: StoredBackgroundRun = {
        runId,
        label: frame.label || held?.label || '',
        state: frame.run_state ?? held?.state ?? 'unknown',
        parentActivityId: frame.parent_id || held?.parentActivityId,
        startedAt: frame.started_at ?? held?.startedAt ?? observedAt,
        finishedAt: frame.finished_at || held?.finishedAt,
        reason: frame.reason || held?.reason || '',
        stopSupported: frame.stop_supported === true,
        stopUnsupportedReason: frame.stop_unsupported_reason ?? '',
        observedAt,
        updatedSequence: frame.sequence,
      };
      const backgroundRuns = agent.backgroundRuns.slice();
      if (index >= 0) backgroundRuns[index] = run;
      else backgroundRuns.push(run);
      return {
        ...agent,
        sequence: frame.sequence,
        backgroundRuns: index >= 0 ? backgroundRuns : evictBackgroundRuns(backgroundRuns),
      };
    }
    default:
      return agent;
  }
}

/** Restates one agent's snapshot in the shape the live rail folds into. */
function fromSnapshot(
  snapshot: AgentActivitySnapshot,
  observedAt: number,
  held?: AgentPanelState,
): AgentPanelState {
  return {
    sequence: snapshot.sequence,
    // The server's own line is the inverse of the same rule: it reads idle from
    // a turn that is not running and nothing else, so idle is exactly a turn
    // that has ended.
    turnActive: snapshot.state !== 'idle',
    plan: snapshot.plan ? snapshot.plan.map(entry => ({ ...entry })) : [],
    activities: (snapshot.activities ?? []).map(entry => ({
      id: entry.id,
      label: entry.label ?? '',
      description: entry.description ?? '',
      kind: entry.kind ?? '',
      status: entry.status,
      detail: entry.detail ?? '',
      paths: entry.paths ? [...entry.paths] : [],
      parentId: entry.parent_id ?? '',
      subagent: entry.subagent === true,
      background: entry.background === true,
      backgroundState: entry.background_state ?? '',
      followUpControl: entry.follow_up_control ?? '',
      reason: entry.reason ?? '',
    })),
    answer: snapshot.answer ?? '',
    reasoning: snapshot.reasoning ?? '',
    backgroundRuns: evictBackgroundRuns((snapshot.background_runs ?? []).map(run => {
      const incoming: BackgroundRun = {
        runId: run.run_id,
        label: run.label,
        state: run.state,
        parentActivityId: run.parent_id,
        startedAt: run.started_at,
        finishedAt: run.finished_at,
        reason: run.reason ?? '',
        stopSupported: run.stop_supported,
        stopUnsupportedReason: run.stop_unsupported_reason ?? '',
      };
      const previous = held?.backgroundRuns.find(candidate => candidate.runId === incoming.runId);
      const unchanged = previous !== undefined && sameBackgroundRun(previous, incoming);
      return {
        ...incoming,
        observedAt: unchanged ? previous.observedAt : observedAt,
        updatedSequence: unchanged ? previous.updatedSequence : snapshot.sequence,
      };
    })),
  };
}

/**
 * What every agent is doing in every channel the reader can see, held for as
 * long as it is worth looking at and written nowhere.
 *
 * The strip is the only subscriber. Nothing that renders the message list may
 * read this store: `MessageList` is not memoised and rebuilds the identity that
 * busts every row's memo, so an activity report reaching the channel view would
 * cost a full-channel markdown re-parse several times a second.
 */
export const useAgentActivityStore = create<AgentActivityState>()((set) => ({
  byChannel: {},

  applyFrame: (frame, observedAt = Date.now()) => set((s) => {
    const agents = s.byChannel[frame.channel_id];
    const agent = agents?.[frame.agent_id];
    // The bus guarantees nothing about order or duplication, so anything not
    // newer than what this agent already said is not applied at all.
    if (agent && frame.sequence <= agent.sequence) return s;

    // An agent heard from for the first time takes this report's counter even
    // when the report itself changes nothing, or the next stale report would
    // measure itself against a counter that never started.
    const folded = foldFrame(agent ?? { ...EMPTY_AGENT, sequence: frame.sequence }, frame, observedAt);
    if (agent && folded === agent) return s;

    return {
      byChannel: {
        ...s.byChannel,
        [frame.channel_id]: { ...agents, [frame.agent_id]: folded },
      },
    };
  }),

  /**
   * Takes a channel's whole answer, keeping only what an agent has said since
   * the question was asked.
   *
   * The socket subscribes to every channel the reader belongs to long before
   * one is opened, so reports routinely land while this request is in flight.
   * Comparing sequences would not settle that on its own: an agent whose plugin
   * reconnected starts its counter again, so a lower number is as likely to
   * mean "a new connection" as "an older report". What the answer cannot know
   * about is exactly what arrived after it was asked for, so that is what is
   * kept and everything else is replaced.
   *
   * Background runs use the same baseline at run granularity. The snapshot
   * supplies runs the browser had not seen, while a run changed after the
   * request was sent keeps its live value.
   *
   * An agent this answer does not carry is one the server has forgotten — its
   * connection ended, or it fell silent past the expiry window — and it goes,
   * under the same rule. Nothing else would ever drop it: the rail carries
   * upserts and never a removal.
   */
  applySnapshot: (channelId, snapshots, askedWith, observedAt = Date.now()) => set((s) => {
    const held = s.byChannel[channelId] ?? {};
    const spokeSinceAsking = (agentId: string) =>
      held[agentId] !== undefined && held[agentId].sequence !== askedWith[agentId]?.sequence;

    const next: Record<string, AgentPanelState> = {};
    for (const snapshot of snapshots) {
      const current = held[snapshot.agent_id];
      const incoming = fromSnapshot(snapshot, observedAt, current);
      if (!current || !spokeSinceAsking(snapshot.agent_id)) {
        next[snapshot.agent_id] = incoming;
        continue;
      }

      next[snapshot.agent_id] = {
        ...current,
        backgroundRuns: mergeSnapshotBackgroundRuns(
          incoming.backgroundRuns,
          current.backgroundRuns,
          askedWith[snapshot.agent_id],
        ),
      };
    }
    for (const agentId of Object.keys(held)) {
      if (next[agentId] === undefined && spokeSinceAsking(agentId)) next[agentId] = held[agentId];
    }

    const heldIds = Object.keys(held);
    const unchanged =
      heldIds.length === Object.keys(next).length &&
      heldIds.every(agentId => next[agentId] === held[agentId]);
    if (unchanged) return s;

    return { byChannel: { ...s.byChannel, [channelId]: next } };
  }),

  clearExpiredBackgroundRuns: (now = Date.now()) => set((s) => {
    let changed = false;
    const byChannel = { ...s.byChannel };

    for (const [channelId, agents] of Object.entries(s.byChannel)) {
      let nextAgents = agents;
      for (const [agentId, agent] of Object.entries(agents)) {
        const backgroundRuns = pruneExpiredBackgroundRuns(agent.backgroundRuns, now);
        if (backgroundRuns === agent.backgroundRuns) continue;
        if (nextAgents === agents) nextAgents = { ...agents };
        nextAgents[agentId] = { ...agent, backgroundRuns };
        changed = true;
      }
      if (nextAgents !== agents) byChannel[channelId] = nextAgents;
    }

    return changed ? { byChannel } : s;
  }),

  clearChannel: (channelId) => set((s) => {
    if (!s.byChannel[channelId]) return s;
    const { [channelId]: _dropped, ...rest } = s.byChannel;
    return { byChannel: rest };
  }),

  reset: () => set({ byChannel: {} }),
}));
